/**
 * Domain percakapan: antrean DM & komentar yang bot tidak sanggup jawab.
 *
 * Rutenya BARU (8 September) — sebelumnya tidak ada endpoint yang mengembalikan
 * daftar antrean dalam bentuk apa pun. Alur Telegram mendorong notifikasi begitu
 * sesuatu datang; halaman web membalik arahnya jadi menarik, dan arah itu butuh
 * rute sendiri.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, kueri } from '../../shared/api/client'

export const kunci = {
  antrean: (saring) => ['engagement', 'antrean', saring],
}

export function useAntrean(saring = {}) {
  return useQuery({
    queryKey: kunci.antrean(saring),
    queryFn: () => api.get(`/dashboard/engagements${kueri(saring)}`),
  })
}

/**
 * Mengambil DM. Identitas pengklaim TIDAK dikirim dari sini — backend membacanya
 * dari `uid` di dalam JWT (ADR-044). Badan permintaan sengaja kosong: apa pun yang
 * dikirim browser sebagai identitas akan diabaikan, dan lebih jujur tidak
 * mengirimnya sama sekali daripada mengirim sesuatu yang pura-pura berpengaruh.
 */
export function useAmbil() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.post(`/engagement/${id}/claim`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['engagement'] }),
  })
}

/**
 * Membalas. Satu rute yang mengerjakan seluruh rantainya di server — simpan, kirim
 * ke Instagram, tandai terkirim.
 *
 * Tujuan pengiriman tidak ada di sini, dan itu disengaja: ia dibaca backend dari
 * baris antreannya sendiri. Rute pengiriman mentah (`/instagram/send-dm`) menerima
 * `recipient_id` bebas dan TIDAK dibuka ke browser — kalau ia dibuka, siapa pun yang
 * punya login bisa mengirim DM ke akun mana pun.
 */
export function useBalas() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, teks }) => api.post(`/dashboard/engagements/${id}/balas`, { teks }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['engagement'] }),
  })
}

/**
 * Menyatakan satu percakapan TIDAK PERLU DIBALAS.
 *
 * Tidak mengirim apa pun ke Instagram — orang di seberang tidak diberi tahu apa pun.
 * Yang berubah cuma statusnya, jadi ia keluar dari "Perlu dijawab" dan tetap bisa
 * dilihat lewat penyaring status di papan engagement.
 */
export function useAbaikan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.post(`/dashboard/engagements/${id}/abaikan`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['engagement'] }),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// PERCAKAPAN PER ORANG (rancang ulang, K2 · ADR-094 no. 7).
//
// Rute di atas bekerja per PESAN dan tetap dipakai layar lama. Yang di bawah bekerja per
// PERCAKAPAN: satu orang di satu platform, dengan semua pesannya. `id` percakapan adalah
// id pesan pertamanya, jadi tidak berubah saat pesan baru masuk; rutenya menerima id
// pesan mana pun dari percakapan itu.
// ─────────────────────────────────────────────────────────────────────────────

export const kunciPercakapan = {
  semua: () => ['percakapan'],
  daftar: (saring) => ['percakapan', 'daftar', saring],
  semuaUtas: () => ['percakapan', 'utas'],
  utas: (id) => ['percakapan', 'utas', id],
}

// Kotak masuk yang terbuka menyegarkan dirinya: pesan baru datang tanpa ada yang
// menekan apa pun. Berhenti selama tabnya tidak terlihat (bawaan TanStack Query).
const SELANG_KOTAK = 30_000

/**
 * Selang penyegaran daftar dan utas: BERHENTI selama kuerinya gagal tanpa pernah berisi.
 *
 * Kueri tanpa isi yang diminta lagi kembali berstatus memuat, jadi tiap putaran mengganti
 * kotak galat dan Coba lagi-nya dengan kerangka sampai percobaannya gagal lagi: tombolnya
 * hilang di bawah jari, dan galat yang sama diumumkan ulang tiap 30 detik. Yang
 * memulihkannya Coba lagi, jendela yang difokus lagi, atau sambungan yang kembali. Kueri
 * yang sudah berisi tetap disegarkan: galatnya tertulis di samping isinya, tidak menggantikannya.
 */
const selangKotak = (kueri) => (
  kueri.state.status === 'error' && kueri.state.data === undefined ? false : SELANG_KOTAK
)

// SATU HITUNGAN "menunggu staf" DI LAYAR. Lencana menu, kartu angka Hari ini, dan subjudul
// Perlu dijawab membaca angka server yang sama, tetapi dari kueri yang berbeda dengan
// putaran yang berbeda (30 dan 60 detik). Tanpa disamakan, lencana menulis 3 di samping
// kartu yang sudah menulis 4, sampai semenit.
//
// Dua arah, dan tidak berputar: jawaban daftar hanya MENULIS cache lencana (tanpa
// permintaan); jawaban lencana yang angkanya berubah meminta ulang daftar yang sedang
// tampil, dan jawaban daftar itu membawa angka yang sama, jadi berhenti di situ.
const SARING_LENCANA = { limit: 1 }
const kueriLencana = (q) => q.queryKey[2]?.limit === SARING_LENCANA.limit

function samakanLencana(qc, d) {
  qc.setQueryData(kunciPercakapan.daftar(SARING_LENCANA), (lama) => (
    lama && (lama.menunggu !== d.menunggu || lama.terlama_at !== d.terlama_at)
      ? { ...lama, menunggu: d.menunggu, terlama_at: d.terlama_at }
      : lama
  ))
}

/**
 * Kotak masuk: percakapan yang menunggu staf atau sedang dipegang. Dengan `cari`,
 * semua percakapan yang cocok, termasuk yang masih dijawab bot.
 *
 * Jawabannya ikut membawa `menunggu` (jumlah percakapan yang menunggu staf, tidak
 * terpengaruh `cari`) dan `terlama_at`.
 */
export function useKotakMasuk(saring = {}, { aktif = true } = {}) {
  const qc = useQueryClient()
  return useQuery({
    queryKey: kunciPercakapan.daftar(saring),
    queryFn: async () => {
      const d = await api.get(`/dashboard/percakapan${kueri(saring)}`)
      samakanLencana(qc, d)
      return d
    },
    enabled: aktif,
    refetchInterval: selangKotak,
    // Daftar lama tetap tampil selagi kata cari berganti, supaya panelnya tidak berkedip
    // kosong di tiap ketikan.
    placeholderData: (lama) => lama,
  })
}

/**
 * Jumlah percakapan yang menunggu staf, untuk lencana menu. Satu baris saja yang
 * diminta: yang dibaca hanya `menunggu`.
 */
export function useJumlahMenunggu() {
  const qc = useQueryClient()
  return useQuery({
    queryKey: kunciPercakapan.daftar(SARING_LENCANA),
    queryFn: async () => {
      const lama = qc.getQueryData(kunciPercakapan.daftar(SARING_LENCANA))
      const d = await api.get(`/dashboard/percakapan${kueri(SARING_LENCANA)}`)
      if (lama && lama.menunggu !== d.menunggu) {
        // Daftar yang sedang diminta tidak dibatalkan: ia berangkat hampir bersamaan dan
        // akan membawa angka yang sama.
        qc.invalidateQueries(
          { queryKey: ['percakapan', 'daftar'], predicate: (q) => !kueriLencana(q) },
          { cancelRefetch: false },
        )
      }
      return d
    },
    refetchInterval: 2 * SELANG_KOTAK,
    select: (d) => d.menunggu,
  })
}

/** Percakapan utuh satu orang: kepalanya dan semua pesannya, lama ke baru. */
export function useUtas(id) {
  return useQuery({
    queryKey: kunciPercakapan.utas(id),
    queryFn: () => api.get(`/dashboard/percakapan/${id}`),
    enabled: !!id,
    refetchInterval: selangKotak,
  })
}

function mutasiPercakapan(qc, mutationFn) {
  return {
    mutationFn,
    // Disegarkan, bukan ditambal: percakapan yang sama bisa diubah dari Telegram atau
    // oleh orang lain pada saat yang sama, dan jawaban server yang benar.
    onSettled: () => qc.invalidateQueries({ queryKey: kunciPercakapan.semua() }),
  }
}

/** Ambil alih: pemanggil jadi pemegangnya dan bot diam untuk orang itu. */
export function useAmbilAlih() {
  const qc = useQueryClient()
  return useMutation(mutasiPercakapan(qc,
    (id) => api.post(`/dashboard/percakapan/${id}/ambil-alih`, {})))
}

/** Kembalikan ke bot: pegangan dilepas dan bot menjawab lagi mulai pesan berikutnya. */
export function useKembalikanKeBot() {
  const qc = useQueryClient()
  return useMutation(mutasiPercakapan(qc,
    (id) => api.post(`/dashboard/percakapan/${id}/kembalikan`, {})))
}

/**
 * Kirim satu balasan. Boleh berkali-kali selama percakapannya dipegang pemanggil.
 * Penerimanya dibaca backend dari percakapannya sendiri, tidak dikirim dari sini.
 *
 * BERHASIL TIDAK MENUNGGU PENYEGARAN. Balasannya sudah sampai ke pelanggan; kabar
 * "terkirim" yang masih menunggu daftar, lencana, dan utas diminta ulang terbaca sebagai
 * belum terkirim selama itu, dan daftar adalah kueri yang terberat. Jawabannya sudah
 * memuat gelembung dan kepala percakapannya, jadi utasnya ditulis dari sana dan
 * penyegarannya berjalan di latar.
 *
 * GAGAL tetap menunggu penyegaran: yang menulis sebabnya (`jaga` di `Utas.jsx`) memilih
 * tempatnya menurut data segar.
 */
export function useKirimBalasan() {
  const qc = useQueryClient()
  const segarkan = () => qc.invalidateQueries({ queryKey: kunciPercakapan.semua() })
  return useMutation({
    mutationFn: ({ id, teks }) => api.post(`/dashboard/percakapan/${id}/balas`, { teks }),
    onSuccess: (hasil) => {
      // Yang melempar di sini membuat balasan yang SUDAH terkirim dikabarkan gagal, jadi
      // jawaban yang bentuknya tak terduga dilewati dan penyegaran yang membetulkannya.
      const pesan = hasil?.pesan
      const kepala = hasil?.percakapan
      if (pesan?.id && kepala?.id) {
        // Utas dimuat dengan id pesan MANA PUN dari percakapannya, jadi dicari lewat isinya,
        // bukan lewat kuncinya.
        qc.setQueriesData({ queryKey: kunciPercakapan.semuaUtas() }, (lama) => {
          if (lama?.id !== kepala.id) return undefined
          const lamaPesan = lama.pesan ?? []
          return {
            ...lama,
            ...kepala,
            // Penyegaran berkala bisa mendahului jawaban ini dan sudah membawa gelembungnya.
            pesan: lamaPesan.some((m) => m.id === pesan.id) ? lamaPesan : [...lamaPesan, pesan],
          }
        })
      }
      // Janjinya TIDAK dikembalikan: yang dikembalikan ditunggu sebelum mutasinya selesai.
      segarkan()
    },
    onError: segarkan,
  })
}

/**
 * Tidak perlu dibalas: semua pesan yang menunggu di percakapan itu ditandai, tanpa
 * mengirim apa pun. Jawabannya memuat `diabaikan`, id yang diubah, untuk pembaliknya.
 */
export function useTidakPerluDibalas() {
  const qc = useQueryClient()
  return useMutation(mutasiPercakapan(qc,
    (id) => api.post(`/dashboard/percakapan/${id}/abaikan`, {})))
}

/**
 * Yang ikut terhapus kalau orang di balik percakapan ini dihapus (ADR-125). Tenant Admin ke
 * atas. Diminta saat pertanyaannya dibuka (`aktif`), tidak bersama utasnya, dan selalu segar:
 * jumlah yang kemarin bukan yang akan dihapus hari ini.
 */
export function usePratinjauHapusOrang(id, { aktif = false } = {}) {
  return useQuery({
    queryKey: ['percakapan', 'hapus', id],
    queryFn: () => api.get(`/dashboard/percakapan/${id}/hapus`),
    enabled: !!id && aktif,
    staleTime: 0,
    gcTime: 0,
  })
}

/**
 * Hapus orang di balik percakapan ini permanen, beserta prospeknya (ADR-125).
 *
 * Percakapannya dibuang dari daftar yang sudah dimuat SEBELUM daftar diminta ulang. Tanpa
 * itu layar masih memilihnya selagi daftar baru belum datang, meminta utasnya lagi, dan
 * jawaban 404-nya tampil sebagai galat. Semua percakapan orang itu ikut (DM dan komentar),
 * jadi yang dibuang semua baris ber-`sender_id` sama. Prospeknya juga hilang.
 */
export function useHapusOrang() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }) => api.del(`/dashboard/percakapan/${id}`),
    onSuccess: (_, { senderId }) => {
      qc.setQueriesData({ queryKey: ['percakapan', 'daftar'] }, (lama) => (
        lama?.items ? { ...lama, items: lama.items.filter((p) => p.sender_id !== senderId) } : lama
      ))
      qc.invalidateQueries({ queryKey: ['percakapan', 'daftar'] })
      qc.invalidateQueries({ queryKey: ['prospek'] })
    },
  })
}

/** Pembalik Tidak perlu dibalas: `ids` dari jawaban `useTidakPerluDibalas`. */
export function useUrungkanTidakPerlu() {
  const qc = useQueryClient()
  return useMutation(mutasiPercakapan(qc,
    ({ id, ids }) => api.post(`/dashboard/percakapan/${id}/abaikan/urungkan`, { ids })))
}
